/**
 * Store accent colors are picked freely in the store form (`<input type="color">`),
 * so nothing stops a store from choosing white — which used to render the public
 * return page's reference number as invisible text on a white card.
 *
 * `readableAccent` keeps the store's hue but darkens it until it clears the WCAG
 * AA contrast threshold against the page background, which on the public pages is
 * always light (they don't use the `.dark` variant).
 */

const HEX = /^#(?:[0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/** Minimum contrast against the background — AA for normal-size text. */
const MIN_CONTRAST = 4.5;

type Rgb = [number, number, number];

function parseHex(raw: string): Rgb | null {
  const v = raw.trim();
  if (!HEX.test(v)) return null;
  let hex = v.slice(1);
  // #rgb / #rgba → expand each nibble; drop alpha, it can't help contrast.
  if (hex.length === 3 || hex.length === 4) {
    hex = hex
      .slice(0, 3)
      .split("")
      .map((c) => c + c)
      .join("");
  }
  hex = hex.slice(0, 6);
  return [
    parseInt(hex.slice(0, 2), 16),
    parseInt(hex.slice(2, 4), 16),
    parseInt(hex.slice(4, 6), 16),
  ];
}

function toHex([r, g, b]: Rgb): string {
  return "#" + [r, g, b].map((c) => Math.round(c).toString(16).padStart(2, "0")).join("");
}

/** WCAG relative luminance, 0 (black) to 1 (white). */
export function relativeLuminance([r, g, b]: Rgb): number {
  const [rl, gl, bl] = [r, g, b].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  }) as Rgb;
  return 0.2126 * rl + 0.7152 * gl + 0.0722 * bl;
}

/** WCAG contrast ratio between two colors, 1 (identical) to 21 (black on white). */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/**
 * Return an accent color guaranteed to be legible on `background`.
 *
 * Invalid or missing input falls back to `fallback`; a valid but too-light color
 * is darkened step by step, preserving its hue, until it passes AA.
 */
export function readableAccent(
  raw: string | null | undefined,
  fallback = "#b54a2b",
  background = "#ffffff",
): string {
  const bg = parseHex(background) ?? [255, 255, 255];
  let rgb = parseHex(raw || "") ?? parseHex(fallback) ?? [0, 0, 0];

  for (let i = 0; i < 24 && contrastRatio(rgb, bg) < MIN_CONTRAST; i++) {
    // Pure white has no hue to preserve, so nudge it off zero before scaling.
    if (rgb[0] === 0 && rgb[1] === 0 && rgb[2] === 0) break;
    rgb = rgb.map((c) => Math.max(0, Math.floor(c * 0.85))) as Rgb;
  }

  return toHex(rgb);
}
